import { BorrowerContractFunnelV2Dto } from "api/pricing"
import { FileTypeEnum } from "api/documents"
import { Button } from "components/ui/Button"
import { CheckIcon } from "components/icons/Icons"
import { EyeIcon, AlertTriangle } from "lucide-react"
import { FC, useCallback, useMemo } from "react"
import { Sizes } from "enums/sizes"
import { useBorrowerFunnelPricing } from "./useBorrowerFunnelPricing"
import { useFunnelCardFooter } from "./useFunnelCardFooter"
import { useMainBorrowerPricingState } from "../../contexts/main-borrower-pricing/MainBorrowerPricingContext"
import { useDialog } from "hooks/useDialog"
import {
  GroupSelectionDialogContent,
  GROUP_SELECTION_DIALOG_CONFIG
} from "./GroupSelectionDialogContent"

interface FunnelCardFooterProps {
  contractFunnel: BorrowerContractFunnelV2Dto
}

export const BorrowerFunnelCardFooter: FC<FunnelCardFooterProps> = ({ contractFunnel }) => {
  const { isInShoppingCart, hasBorrowerAlreadyInCart, toggleCart, isDownloading, viewMNCAPQuote } = useFunnelCardFooter(contractFunnel);
  const { renderInitialPrice, renderMonthlyPrice, renderYearlyPrice, renderClientPrice, isPricingLoading } = useBorrowerFunnelPricing(contractFunnel);
  const { borrowerId, borrowerProjectId } = useMainBorrowerPricingState();
  const { openDialog, closeDialog } = useDialog();

  const showAlreadyInCartDialog = useCallback(() => {
    openDialog(
      { title: 'Ajout impossible', size: Sizes.sm },
      <div className="flex flex-col items-center gap-4 py-4">
        <AlertTriangle className="w-12 h-12 text-amber-500" />
        <p className="text-center text-sm text-gray-600">
          Une solution est déjà sélectionnée pour cet emprunteur. Veuillez d'abord retirer la solution existante du panier avant d'en ajouter une nouvelle.
        </p>
        <Button color="primary" onClick={closeDialog} className="w-full">
          Fermer
        </Button>
      </div>
    );
  }, [openDialog, closeDialog]);

  const handleAddToCart = useCallback(() => {
    if (hasBorrowerAlreadyInCart) {
      showAlreadyInCartDialog();
      return;
    }
    if (contractFunnel.hasSelection && borrowerId && borrowerProjectId) {
      openDialog(
        GROUP_SELECTION_DIALOG_CONFIG,
        <GroupSelectionDialogContent
          borrowerProjectId={borrowerProjectId}
          borrowerId={borrowerId}
          onConfirm={(selectedBorrowerIds) => toggleCart(selectedBorrowerIds, true)}
        />
      );
    } else {
      const borrowerIds = contractFunnel.borrowers.map(borrower => borrower.id);
      toggleCart(borrowerIds, contractFunnel.borrowers.length === 2);
    }
  }, [hasBorrowerAlreadyInCart, showAlreadyInCartDialog, contractFunnel.hasSelection, borrowerId, borrowerProjectId, openDialog, toggleCart, contractFunnel.borrowers]);

  const renderDocs = useMemo(() => {
    return (
      <Button
        outline
        color="primary"
        className="w-full"
        disabled={isPricingLoading || isDownloading === FileTypeEnum.DevisPdfGenerer}
        onClick={() => viewMNCAPQuote()}
      >
        <EyeIcon className="w-4 h-4" />
        {isDownloading === FileTypeEnum.DevisPdfGenerer
          ? 'Chargement...'
          : 'Voir le devis'
        }
      </Button>
    )
  }, [isDownloading, viewMNCAPQuote, isPricingLoading])

  return (
    <div className="flex items-center justify-center flex-col gap-2 py-4 px-6 flex-none">
      <div className="flex flex-col items-end justify-end">
        <div className="flex gap-1 text-md 2xl:text-2xl font-extrabold">
          <span className="flex items-center gap-2">
            {renderInitialPrice}
            {renderYearlyPrice}
          </span>
        </div>
        <p className="flex text-xs 2xl:text-lg gap-1">
          soit {renderClientPrice} €
          <span className="text-xs 2xl:text-lg text-gray-400"> / sur 8 ans</span>
        </p>
        <p className="flex text-xs 2xl:text-lg gap-1">
          soit {renderMonthlyPrice} €
          <span className="text-xs 2xl:text-lg text-gray-400"> / par mois</span>
        </p>
      </div>

      <Button
        color="secondary"
        className={`w-full ${isInShoppingCart ? "opacity-80" : ""}`}
        onClick={handleAddToCart}
        disabled={isPricingLoading}
      >
        {isInShoppingCart ? "Ajouté" : "Ajouter à la sélection"}
        {isInShoppingCart && <CheckIcon />}
      </Button>

      {renderDocs}
    </div>
  )
}
